import { useState, type ReactNode } from "react";
import { IS_PREVIEW } from "../data/transport.js";
import { Lettering } from "./primitives.js";

/**
 * Shown only in a static preview build. A viewer who lands on a deployed
 * preview has no way to know that the engine, the database and the models
 * are absent — and the product's whole claim is that those are where the
 * truth lives, so letting someone form a judgement about it from a page that
 * cannot roll a die would be the wrong kind of demo.
 *
 * Written in the frames' own vocabulary: a hairline, micro type,
 * `--pc-ink-soft`, one lowercase affordance with a trailing period. It is
 * dismissible, because after the first read it is furniture.
 */
export function PreviewNote(): ReactNode {
  const [dismissed, setDismissed] = useState(false);
  if (!IS_PREVIEW || dismissed) return null;

  return (
    <aside className="fixed right-0 bottom-0 left-0 z-40 border-t border-t-ink-soft bg-paper px-gutter py-3">
      <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1 font-sans text-micro text-ink-soft">
        <span className="font-display font-bold text-ink">Static preview.</span>
        <span>
          The screens, the design system and all forty case documents are real. The engine, the database and the models
          are not here, so a turn cannot be taken — that needs <code className="font-mono">pnpm dev</code>.
        </span>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          className="ml-auto cursor-pointer border-0 bg-transparent p-0 font-sans text-micro text-ink hover:bg-marker"
        >
          <Lettering text="understood." />
        </button>
      </div>
    </aside>
  );
}
